import React, { Fragment } from 'react';
import styled from 'styled-components';
import {
  FilterType,
  IConditionFieldsFragment,
  IFilterFieldsFragment,
  INamedFilter,
} from '../../../utils/apollo/graphqlSchema';
import PredicateContent from './PredicateContent';
import { theme } from '../../../styles/theme';
import { ConnectorWrapper } from '../../Builder/BuilderUI';
import * as BuilderInfoUI from '../BuilderInfoUI';

const Container = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  column-gap: 6px;
  row-gap: 4px;
  line-height: normal;
`;

const Condition = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  column-gap: 4px;
  color: ${theme.colors.mainBlue};
`;

const ConditionBracket = styled.div`
  flex: 0 0 auto;
  font-weight: 500;
`;

const Connector = styled(ConnectorWrapper)`
  padding: 1px 4px;
  font-size: inherit;
`;

interface IProps {
  filter: Pick<IFilterFieldsFragment, 'type' | 'logicOps' | 'conditions'>;
  showExpanded?: boolean;
}

// Display read only filters.
export default ({ filter, showExpanded = false }: IProps) => {
  const renderConnector = (text: string) => <Connector>{text}</Connector>;

  const renderPredicates = (condition: IConditionFieldsFragment) => {
    const { predicates } = condition;

    return predicates.map((predicate, index) => {
      return (
        <Fragment key={index}>
          {index > 0 &&
            renderConnector(condition.logicOps[index - 1].name.toUpperCase())}
          <PredicateContent predicate={predicate} index={index} />
        </Fragment>
      );
    });
  };

  if (filter.type === FilterType.Named && !showExpanded) {
    const { name, isDisabled, isLatestVersion } = filter as INamedFilter;

    return (
      <BuilderInfoUI.VersionedEntityPopupLabel
        $isDisabled={isDisabled}
        $isLatestVersion={isLatestVersion}
      >
        {name}
      </BuilderInfoUI.VersionedEntityPopupLabel>
    );
  }

  const { conditions, logicOps } = filter;

  return (
    <Container>
      {conditions.map((condition, index) => (
        <Fragment key={`condition-${index}`}>
          <Condition>
            <ConditionBracket>(</ConditionBracket>
            {renderPredicates(condition)}
            <ConditionBracket>)</ConditionBracket>
          </Condition>
          {index < logicOps.length &&
            renderConnector(logicOps[index].name.toUpperCase())}
        </Fragment>
      ))}
    </Container>
  );
};
